/**
 * 主题上下文 - 创美药业设计体系
 * 
 * 功能特性：
 * - 浅色模式专用（简洁、高级、诗意）
 * - 集成 Ant Design 5.x ConfigProvider
 * - 基于 Design Token 系统 v3.0
 * - 蓝色主题色系
 */

import React, { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import { 
  colorTokens,
  generateCSSVariables,
  getTheme
} from '../styles/tokens';
import { getAntdTheme } from '../styles/theme/antdTheme';

// 主题上下文类型定义
interface ThemeContextType {
  /** 当前颜色 Token */
  colorTokens: typeof colorTokens;
}

// 创建主题上下文
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// 主题 Provider 属性
interface ThemeProviderProps {
  children: ReactNode;
  /** 默认主题模式（仅支持 light） */
  defaultTheme?: 'light';
}

/**
 * 主题 Provider 组件
 * 
 * 提供浅色主题配置和 Ant Design ConfigProvider 集成
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children
}) => {
  // 更新 HTML 根元素的主题类名和 CSS 变量
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    
    // 添加主题类名
    root.classList.add('theme-light');
    root.setAttribute('data-theme', 'light');
    
    // 应用 Design Token 生成的 CSS 变量
    const designTokens = getTheme('light');
    const cssVariables = generateCSSVariables(designTokens);
    
    // 应用 CSS 变量到根元素
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, []);

  // 颜色 Token
  const currentColorTokens = useMemo(() => colorTokens, []);
  
  // 获取 Ant Design 主题配置 - 使用 useMemo 优化
  const antdThemeConfig = useMemo(() => getAntdTheme('light'), []);

  // 主题上下文值 - 使用 useMemo 优化
  const contextValue: ThemeContextType = useMemo(() => ({
    colorTokens: currentColorTokens,
  }), [currentColorTokens]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={antdThemeConfig}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

/**
 * 使用主题的 Hook
 * 
 * @returns 主题上下文值
 * @throws 如果在 ThemeProvider 外部使用则抛出错误
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};


// 导出主题相关类型
export type { ThemeContextType, ThemeProviderProps };